/** Period-based owner and Project Manager analytics, sourced from one scoped API. */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowPathIcon, BanknotesIcon, ClipboardDocumentCheckIcon, MapPinIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { apiGet } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

type AnalyticsData = {
  period?: { from: string; to: string };
  sections?: {
    money?: Record<string, number>;
    work?: { active?: number; awaiting_review?: number; completed?: number; overdue?: number };
    visits?: { total?: number; plots_visited?: number };
    issues?: { open_total?: number; reported_in_period?: number };
  };
  unavailable?: string[];
};

const money = (value?: number, currency = 'RWF') =>
  `${Number(value || 0).toLocaleString()} ${currency}`;

const Metric = ({ label, value, href, note }: { label: string; value: string; href: string; note?: string }) => (
  <Link to={href} className="block rounded-lg border border-slate-200 bg-white p-4 transition hover:border-emerald-400 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-600">
    <p className="text-xs font-bold text-slate-500">{label}</p>
    <p className="mt-1 text-xl font-black text-slate-900">{value}</p>
    {note && <p className="mt-1 text-xs text-slate-500">{note}</p>}
  </Link>
);

const Section = ({ title, icon: Icon, href, children }: React.PropsWithChildren<{ title: string; icon: React.ElementType; href: string }>) => (
  <section className="mt-5">
    <div className="mb-3 flex items-center justify-between">
      <h2 className="flex items-center gap-2 text-base font-black text-slate-900"><Icon className="h-5 w-5 text-emerald-700" />{title}</h2>
      <Link to={href} className="text-sm font-bold text-emerald-700 hover:underline">Open records</Link>
    </div>
    {children}
  </section>
);

const AnalyticsPage: React.FC = () => {
  const { hasFeature, canAccessPage, user } = useAuth();
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestVersion = useRef(0);

  const sections = useMemo(() => ({
    money: hasFeature('farm_finance') && canAccessPage('farm-finance'),
    work: hasFeature('activities') && canAccessPage('activities'),
    visits: hasFeature('field_diary') && canAccessPage('field-diary'),
    issues: hasFeature('issues') && canAccessPage('issues'),
  }), [hasFeature, canAccessPage]);

  const load = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    setError(null);
    try {
      const result = await apiGet<AnalyticsData>(`/api/owner/analytics?month=${encodeURIComponent(month)}`);
      if (version === requestVersion.current) setData(result);
    } catch (e: any) {
      if (version === requestVersion.current) setError(e?.message || 'Analytics data is unavailable.');
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    void load();
    return () => { requestVersion.current += 1; };
  }, [load, sections.money, sections.work, sections.visits, sections.issues]);

  const available = data?.sections || {};
  const unavailable = data?.unavailable || [];
  const periodQuery = `?month=${encodeURIComponent(month)}`;
  const hasSection = sections.money || sections.work || sections.visits || sections.issues;
  const workHref = (scope: string) => user?.role === 'owner'
    ? `/dashboard/owner-work?scope=${scope}&month=${encodeURIComponent(month)}`
    : `/dashboard/activities?tab=assignments&status=${scope}`;

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-black text-slate-900">Analytics</h1>
            <p className="mt-1 text-sm text-slate-600">Workspace activity for the selected reporting month.</p>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="analytics-month" className="text-sm font-semibold text-slate-600">Period</label>
            <input id="analytics-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800" />
            <button type="button" onClick={() => void load()} disabled={loading} aria-label="Refresh analytics" className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {error && <div role="alert" className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800"><span>{error}</span><button type="button" onClick={() => void load()} className="font-black underline">Retry</button></div>}
        {loading && !data && <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-600">Loading workspace analytics…</div>}

        {!hasSection && <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-900">No operational analytics sections are enabled for this workspace.</div>}

        {sections.money && (
          <Section title="Money" icon={BanknotesIcon} href={`/dashboard/money${periodQuery}`}>
            {unavailable.includes('money') ? <Unavailable /> : available.money ? (
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Metric label="Funding sent" value={money(available.money.funding_sent)} href={`/dashboard/money${periodQuery}`} />
                <Metric label="Recorded payments" value={money(available.money.recorded_payments)} href={`/dashboard/money${periodQuery}`} note="Payment events, not approvals" />
                <Metric label="Awaiting review" value={money(available.money.awaiting_review)} href={`/dashboard/money${periodQuery}&status=pending`} />
                <Metric label="Recorded balance" value={money(available.money.recorded_balance)} href={`/dashboard/money${periodQuery}`} note="Not bank-verified" />
              </div>
            ) : <Unavailable />}
          </Section>
        )}

        {sections.work && (
          <Section title="Work" icon={ClipboardDocumentCheckIcon} href="/dashboard/activities">
            {unavailable.includes('work') ? <Unavailable /> : available.work ? (
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Metric label="Active work" value={Number(available.work.active || 0).toLocaleString()} href={workHref('active')} />
                <Metric label="Awaiting review" value={Number(available.work.awaiting_review || 0).toLocaleString()} href={workHref('awaiting_review')} />
                <Metric label="Completed" value={Number(available.work.completed || 0).toLocaleString()} href={workHref('completed')} />
                <Metric label="Overdue" value={Number(available.work.overdue || 0).toLocaleString()} href={workHref('overdue')} />
              </div>
            ) : <Unavailable />}
          </Section>
        )}

        {(sections.visits || sections.issues) && (
          <Section title="Field activity" icon={MapPinIcon} href={sections.visits ? '/dashboard/field-diary' : '/dashboard/issues'}>
            {((sections.visits && unavailable.includes('visits')) || (sections.issues && unavailable.includes('issues'))) ? <Unavailable /> : (
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {sections.visits && available.visits && <>
                  <Metric label="Field visits" value={Number(available.visits.total || 0).toLocaleString()} href="/dashboard/field-diary" />
                  <Metric label="Plots visited" value={Number(available.visits.plots_visited || 0).toLocaleString()} href="/dashboard/field-diary" />
                </>}
                {sections.issues && available.issues && <>
                  <Metric label="Open issues" value={Number(available.issues.open_total || 0).toLocaleString()} href="/dashboard/issues" />
                  <Metric label="Reported this period" value={Number(available.issues.reported_in_period || 0).toLocaleString()} href="/dashboard/issues" />
                </>}
                {((sections.visits && !available.visits) || (sections.issues && !available.issues)) && <Unavailable />}
              </div>
            )}
          </Section>
        )}

        {data?.period && <p className="mt-6 text-xs text-slate-500">Reporting period: {data.period.from} to {data.period.to}. Disabled workspace sections are not queried or shown.</p>}
      </div>
    </main>
  );
};

const Unavailable = () => (
  <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-900">
    <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />This section could not be loaded. Other available sections remain usable.
  </div>
);

export default AnalyticsPage;
